// ChatBody.jsx
import React, { useState, useCallback, memo, useRef, useEffect } from "react";

import {
  LinkIcon,
  CheckDoubleIcon,
  CheckIcon, // For 'Sent' status
  PlayIcon,
  CheckSmallFilledIcon,
  CloseIcon, // For unpinning
} from "./ChatIcons";

import { AudioMessage } from "./AudioMessage";
import { ImageCarousel } from "./ImageCarousel";
import { FileAttachment } from "./FileAttachment";
import { Loader } from "@mantine/core";
// ---

const ChatBody = React.forwardRef((props, ref) => {
  const {
    messages,
    pinnedMessage,
    selectModeActive,
    selectedMessageIds,
    // --- Handlers ---
    onContextMenu,
    onPinMessage, // Used for unpinning here
    onGoToMessage, // Assumed handler for clicking pinned message
    // onReplyToMessage, // Optional direct actions
    // onInitiateSelectMode, // Optional direct actions
    onToggleMessageSelection,
    isLoading,
  } = props;

  // Internal state only for the Carousel
  const [carouselState, setCarouselState] = useState({
    isOpen: false,
    items: [],
    currentIndex: 0,
  });
  const scrollableAreaRef = useRef(null);

  // Max visible media items before showing "+X"
  const maxVisibleMedia = 2; // Define max visible media items for attachments

  // Effect to scroll to the bottom when messages change
  useEffect(() => {
    if (scrollableAreaRef.current) {
      scrollableAreaRef.current.scrollTop =
        scrollableAreaRef.current.scrollHeight;
    }
  }, [messages, scrollableAreaRef]);

  // --- Carousel Handlers (Internal) ---
  const openCarousel = useCallback(
    (items, clickedIndexOriginalAttachments) => {
      const viewableItems = items.filter(
        (item) => item.type === "image" || item.type === "video"
      );
      const clickedItemSrc = items[clickedIndexOriginalAttachments]?.src;
      const newIndexInViewable = viewableItems.findIndex(
        (item) => item.src === clickedItemSrc
      );

      if (viewableItems.length > 0 && newIndexInViewable !== -1) {
        setCarouselState({
          isOpen: true,
          items: viewableItems.map((item) => ({
            type: item.type,
            src: item.src,
            alt: item.alt || `${item.type} attachment`,
            thumbnailUrl: item.thumbnailUrl,
          })),
          currentIndex: newIndexInViewable,
        });
      } else if (
        viewableItems.length > 0 &&
        clickedIndexOriginalAttachments >= maxVisibleMedia
      ) {
        const firstHiddenIndexInViewable = viewableItems.findIndex(
          (item, idx) => idx >= maxVisibleMedia
        );
        setCarouselState({
          isOpen: true,
          items: viewableItems.map((item) => ({
            type: item.type,
            src: item.src,
            alt: item.alt,
            thumbnailUrl: item.thumbnailUrl,
          })),
          currentIndex: Math.max(0, firstHiddenIndexInViewable),
        });
      } else if (viewableItems.length > 0) {
        setCarouselState({
          isOpen: true,
          items: viewableItems.map((item) => ({
            type: item.type,
            src: item.src,
            alt: item.alt,
            thumbnailUrl: item.thumbnailUrl,
          })),
          currentIndex: 0,
        });
        console.log(
          "Clicked item not directly viewable or fallback, opening carousel from start."
        );
      } else {
        console.log("No viewable items found for carousel.");
      }
    },
    [maxVisibleMedia]
  ); // Dependency added

  const closeCarousel = useCallback(() => {
    setCarouselState({ isOpen: false, items: [], currentIndex: 0 });
  }, []);

  const handleDeleteCarouselItem = useCallback(
    (indexToDeleteInCarousel) => {
      const currentCarouselItem = carouselState.items[indexToDeleteInCarousel];
      console.log(
        `Request to delete carousel item at index ${indexToDeleteInCarousel}:`,
        currentCarouselItem
      );
      alert(
        "Deletion from carousel needs integration with main message deletion."
      );
      closeCarousel();
    },
    [carouselState.items, closeCarousel]
  );
  // --- End Carousel Handlers ---

  // --- Render Helper Functions ---
  const renderReplyQuote = useCallback(
    (replyTo, isOutgoing) => (
      <div
        className={`border-l-2 pl-3 cursor-pointer outline-none mb-3 ${
          isOutgoing
            ? "border-indigo-400"
            : "border-gray-500"
        }`}
        tabIndex="0"
        aria-label={`reply to: ${replyTo.senderName}`}
      >
        <p
          className={`mb-1 font-semibold text-xs ${
            isOutgoing
              ? "text-indigo-600"
              : "text-black opacity-60"
          } leading-4 tracking-[.01rem]`}
        >
          {replyTo.senderName}
        </p>
        <p
          className={`outline-none text-sm font-normal leading-4 tracking-[.01rem] ${
            isOutgoing
              ? "text-black opacity-50"
              : "text-black opacity-50"
          } line-clamp-2`}
        >
          {replyTo.originalText}
        </p>
      </div>
    ),
    []
  );

  const renderLinkPreview = useCallback(
    (link) => (
      <div className="border-l-2 pl-3 cursor-pointer border-opacity-50 duration-200 border-black mt-5">
        <p className="mb-3 font-semibold text-xs leading-4 tracking-[.01rem] text-black opacity-50">
          {link.title}
        </p>
        <p className="font-normal text-xs leading-4 tracking-[.01rem] text-black opacity-50 line-clamp-3">
          {" "}
          {link.description}{" "}
        </p>
        {link.imageUrl && (
          <img
            src={link.imageUrl}
            className="my-5 rounded-sm max-h-32 w-auto"
            alt={`${link.title} Preview`}
          />
        )}
        <a
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-normal text-xs leading-4 tracking-[.01rem] flex items-center text-black opacity-50 hover:underline"
        >
          {/* --- SVG Replaced --- */}
          <LinkIcon className="w-4 h-4 mr-2 stroke-1" />
          {link.url ? new URL(link.url).hostname : "link"}
        </a>
      </div>
    ),
    []
  );

  const renderReadReceipt = useCallback((status) => {
    let IconComponent;
    let iconClass = "w-4 h-4"; // Default size
    let color = "text-gray-400";
    let title = "Sent";

    if (status === "delivered" || status === "read") {
      // --- Use Imported Icon ---
      IconComponent = CheckDoubleIcon;
      title = status === "delivered" ? "Delivered" : "Read";
      if (status === "read") {
        color = "text-blue-500";
      }
    } else {
      // Sent
      // --- Use Imported Icon ---
      IconComponent = CheckIcon;
      iconClass = "w-3.5 h-3.5"; // Specific size for single check
    }

    return (
      <div
        title={title}
        className={`${color} ml-1 flex-shrink-0 self-end mb-1`}
      >
        <IconComponent className={iconClass} />
      </div>
    );
  }, []); // No dependencies needed if CheckDoubleIcon and CheckIcon are stable imports

  const renderAttachments = useCallback(
    (attachments, messageId, isOutgoing) => {
      const mediaItems = attachments.filter(
        (att) => att.type === "image" || att.type === "video"
      );
      const audioItems = attachments.filter((att) => att.type === "audio");
      const fileItems = attachments.filter((att) => att.type === "file");

      if (
        mediaItems.length === 0 &&
        audioItems.length === 0 &&
        fileItems.length === 0
      )
        return null;

      const maxVisibleMedia = 4;
      const hiddenMediaCount =
        mediaItems.length > maxVisibleMedia
          ? mediaItems.length - maxVisibleMedia
          : 0;

      return (
        <div className="mt-2 space-y-2">
          {mediaItems.length > 0 && (
            <div className="flex flex-wrap -mx-1">
              {mediaItems.slice(0, maxVisibleMedia).map((item, index) => {
                const originalIndex = attachments.findIndex(
                  (att) => att.id === item.id
                );
                return (
                  <div
                    key={`${messageId}-media-${item.id || index}`}
                    className="px-1 mb-2"
                  >
                    <button
                      className="overflow-hidden outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-1 rounded"
                      aria-label={item.alt || `${item.type} attachment`}
                      onClick={() => openCarousel(attachments, originalIndex)}
                    >
                      <div
                        className="relative rounded bg-cover bg-center w-28 h-24 md:w-32 md:h-28 overflow-hidden bg-gray-200"
                        style={{
                          backgroundImage: `url("${
                            item.thumbnailUrl || item.src
                          }")`,
                        }}
                      >
                        {item.type === "video" && (
                          <div className="absolute inset-0 w-full h-full flex justify-center items-center rounded bg-black bg-opacity-20 hover:bg-opacity-10 transition duration-200">
                            <span className="p-2 md:p-3 rounded-full bg-white bg-opacity-40 transition-all duration-200">
                              <PlayIcon className="w-5 h-5 text-white" />
                            </span>
                          </div>
                        )}
                        {index === maxVisibleMedia - 1 &&
                          hiddenMediaCount > 0 && (
                            <div className="absolute inset-0 w-full h-full flex items-center justify-center rounded bg-black bg-opacity-40 text-white hover:bg-opacity-20 transition duration-200 text-lg font-medium">
                              {hiddenMediaCount}+
                            </div>
                          )}
                      </div>
                    </button>
                  </div>
                );
              })}
              {hiddenMediaCount > 0 && (
                <div key={`${messageId}-media-plus`} className="px-1 mb-2">
                  <button
                    className="overflow-hidden outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-1 rounded"
                    aria-label={`View ${hiddenMediaCount} more attachments`}
                    onClick={() => openCarousel(attachments, maxVisibleMedia)}
                  >
                    <div className="relative rounded bg-gray-200 w-28 h-24 md:w-32 md:h-28 overflow-hidden">
                      <div className="absolute inset-0 w-full h-full flex items-center justify-center rounded bg-black bg-opacity-40 text-white hover:bg-opacity-20 transition duration-200 text-lg font-medium">
                        {hiddenMediaCount}+
                      </div>
                    </div>
                  </button>
                </div>
              )}
            </div>
          )}

          {audioItems.length > 0 && (
            <div className="space-y-2">
              {audioItems.map((item, index) => (
                <AudioMessage
                  key={item.url || `${messageId}-audio-${index}`}
                  audioUrl={item.src || item.url}
                  buttonBg={isOutgoing ? "bg-indigo-400" : "bg-indigo-500"}
                />
              ))}
            </div>
          )}

          {fileItems.length > 0 && (
            <div className="space-y-2">
              {fileItems.map((file, index) => (
                <FileAttachment
                  key={file.id || `${messageId}-file-${index}`}
                  file={file}
                />
              ))}
            </div>
          )}
        </div>
      );
    },
    [openCarousel, maxVisibleMedia]
  );

  // --- Render Message Item ---
  const renderMessageItem = useCallback(
    (msg) => {
      const messageId = msg.id;
      const isSelected = selectModeActive && selectedMessageIds.has(messageId);

      const hasTextContent =
        (msg.text && msg.text.trim() !== "") || msg.replyTo || msg.linkPreview;
      const bubbleBaseClasses = `group relative max-w-xs lg:max-w-md rounded-b-lg transition duration-500 ${
        hasTextContent || msg.type === "audio" ? "p-3 md:p-4" : "p-1 md:p-1.5"
      }`;
      const incomingBubbleClasses =
        "rounded-tr-lg mr-2 bg-gray-100";
      const outgoingBubbleClasses =
        "rounded-tl-lg bg-indigo-100";
      const selectionClasses = isSelected
        ? "ring-2 ring-offset-1 ring-blue-400"
        : "";
      const bubbleClasses = `${bubbleBaseClasses} ${
        msg.isOutgoing ? outgoingBubbleClasses : incomingBubbleClasses
      } ${selectionClasses}`;

      const hasText = msg.text && msg.text.trim() !== "";
      const hasAttachments = msg.attachments && msg.attachments.length > 0;
      const hasLinkPreview = msg.linkPreview;
      const hasReply = msg.replyTo;

      // Sub-Components remain the same conceptually
      const RowContainer = ({ children }) => (
        <div
          key={messageId}
          className={`mb-5 flex ${msg.isOutgoing ? "justify-end" : ""}`}
        >
          {children}
        </div>
      );

      const Avatar = () =>
        !msg.isOutgoing ? (
          <div className="mr-3 flex-shrink-0 self-end">
            <button
              aria-label={
                msg.sender.id === -1
                  ? msg.sender.name
                  : `View ${msg.sender.name}'s profile`
              }
              className={`outline-none rounded-full ${
                msg.sender.id === -1
                  ? "cursor-default"
                  : "focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 cursor-pointer"
              }`}
              onClick={() =>
                msg.sender.id !== -1 &&
                (window.location.href = `/profile/user/${msg.sender.id}`)
              }
              disabled={msg.sender.id === -1}
            >
              <div
                className={`w-9 h-9 bg-cover bg-center rounded-full transition-opacity ${
                  msg.sender.id === -1 ? "opacity-60" : "hover:opacity-80"
                }`}
                style={{ backgroundImage: `url("${msg.sender.avatar}")` }}
              />
            </button>
          </div>
        ) : null;
      const MetaContainer = ({ children }) => (
        <div
          className={`flex items-center flex-shrink-0 self-end mb-1 ${
            msg.isOutgoing ? "mr-2" : "ml-2"
          }`}
        >
          {children}
        </div>
      );
      const Timestamp = () => (
        <p className="outline-none text-xs text-black opacity-60 font-light leading-4 tracking-[.01rem] whitespace-pre">
          {msg.timestamp}
        </p>
      );
      const ReadReceipt = () =>
        msg.isOutgoing && !msg.readReceipt
          ? renderReadReceipt(msg.readReceipt)
          : null;
      const TextContent = ({ text }) => {
        const linkRegex = /(https?:\/\/[^\s]+)/g;
        const parts = text.split(linkRegex);
        return (
          <p
            className="text-sm font-normal leading-4 tracking-[.01rem] outline-none text-black opacity-60 px-1 pt-1 whitespace-pre-wrap break-words"
            tabIndex="0"
          >
            {parts.map((part, index) =>
              linkRegex.test(part) ? (
                <a
                  key={index}
                  href={part}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-500 hover:underline"
                >
                  {part}
                </a>
              ) : (
                <React.Fragment key={index}>{part}</React.Fragment>
              )
            )}
          </p>
        );
      };

      const getGCPUrls = (text) => {
        if (!text) return [];
        const gcpRegex = /https:\/\/storage\.googleapis\.com\/[^\s]+/g;
        return text.match(gcpRegex) || [];
      };

      const createGCPLinkPreview = (url) => {
        const fileName = url.split('/').pop() || 'file';
        const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
        
        return {
          title: fileName,
          // description: 'GCP Storage File',
          imageUrl: isImage ? url : null,
          url: url
        };
      };

      const MessageBubble = () => {
        const gcpUrls = getGCPUrls(msg.text);
        
        return (
          <div
            // ... your existing props
            className={bubbleClasses}
            style={{ cursor: selectModeActive ? "pointer" : "default" }}
          >
            {hasReply && renderReplyQuote(msg.replyTo, msg.isOutgoing)}
      
            {msg.type !== "audio" && hasText && <TextContent text={msg.text} />}
      
            {hasAttachments &&
              renderAttachments(msg.attachments, messageId, msg.isOutgoing)}
      
            {/* Use existing renderLinkPreview for GCP URLs */}
            {gcpUrls.map((url, index) => (
              <div key={index} className="mt-2">
                {renderLinkPreview(createGCPLinkPreview(url))}
              </div>
            ))}
      
            {/* Your existing link preview */}
            {msg.type !== "audio" &&
            hasLinkPreview &&
            (hasText || hasAttachments) ? (
              <div className={hasText || hasAttachments ? "mt-2" : ""}>
                {renderLinkPreview(msg.linkPreview)}
              </div>
            ) : null}
      
            {isSelected && (
              <div className="absolute -top-2 -left-2 p-0.5 bg-blue-500 rounded-full text-white pointer-events-none">
                <CheckSmallFilledIcon className="w-3 h-3" />
              </div>
            )}
          </div>
        );
      };

      // --- Assemble Message Item Row ---
      return (
        <RowContainer>
          <Avatar />
          <div
            className={`flex items-end ${msg.isOutgoing ? "" : "max-w-[80%]"}`}
          >
            {msg.isOutgoing ? (
              <>
                <MetaContainer>
                  {" "}
                  <ReadReceipt /> <Timestamp />{" "}
                </MetaContainer>
                <MessageBubble />
              </>
            ) : (
              <>
                <MessageBubble />
                <MetaContainer>
                  {" "}
                  <Timestamp /> <ReadReceipt />{" "}
                </MetaContainer>
              </>
            )}
          </div>
        </RowContainer>
      );
    },
    [
      selectModeActive,
      selectedMessageIds,
      renderReadReceipt,
      renderReplyQuote,
      renderLinkPreview,
      renderAttachments,
      onContextMenu,
      onToggleMessageSelection,
    ]
  ); // Dependencies

  // --- RENDER CHAT BODY ---
  return (
    <>
      {isLoading ? (
        <Loader
          className="mx-auto mt-10"
          color="indigo"
          size="lg"
          variant="dots"
        />
      ) : (
        <div
          ref={ref}
          className="bg-white relative flex-grow h-full overflow-hidden"
          onClick={() => {
            if (selectModeActive) return;
            onContextMenu(null, null);
          }}
        >
          {/* Main scrolling chat area */}
          <div
            ref={scrollableAreaRef}
            className="h-full grow px-5 py-5 flex flex-col overflow-y-scroll scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
          >
            {/* Pinned Message Display */}
            {pinnedMessage && (
              <div className="sticky top-0 z-10 mb-2 w-full px-4 py-2 bg-white flex items-center justify-between shadow-sm border-b border-gray-200">
                <div
                  className="border-l-2 pl-3 cursor-pointer outline-none border-opacity-50 duration-200 border-gray-900"
                  onClick={() =>
                    onGoToMessage && onGoToMessage(pinnedMessage.id)
                  }
                  tabIndex={0}
                  aria-label={`Pinned message from ${
                    pinnedMessage.author
                  }: ${pinnedMessage.text.substring(0, 50)}...`}
                  title="Go to pinned message"
                >
                  <p className="mb-0.5 font-semibold text-xs leading-4 tracking-[.01rem] text-black opacity-60">
                    {pinnedMessage.author || "Pinned"}
                  </p>
                  <p className="outline-none text-sm font-normal leading-4 tracking-[.01rem] text-black opacity-70">
                    {pinnedMessage.text.length > 70
                      ? `${pinnedMessage.text.substring(0, 70)}...`
                      : pinnedMessage.text}
                  </p>
                </div>
                <div className="flex">
                  <button
                    onClick={() => onPinMessage(pinnedMessage.id)} // onPinMessage likely toggles, so calling it unpins
                    className="group flex justify-center items-center rounded-full outline-none focus:outline-none hover:bg-gray-100 focus:bg-gray-100 transition-all duration-200 w-7 h-7 text-gray-500"
                    title="Unpin message"
                    aria-label="Unpin message"
                  >
                    {/* --- SVG Replaced --- */}
                    <CloseIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
            <div>
              {/* Map messages */}
              {messages.map((msg) =>
                msg.type === "date" ? (
                  <div
                    key={msg.id}
                    className="w-full my-5 flex items-center justify-center"
                  >
                    {" "}
                    <div className="w-full h-0 border-t border-dashed border-gray-300"></div>{" "}
                    <p className="outline-none text-xs font-light text-gray-500 leading-4 tracking-tight mx-4 flex-shrink-0">
                      {" "}
                      {msg.date}{" "}
                    </p>{" "}
                    <div className="w-full h-0 border-t border-dashed border-gray-300"></div>{" "}
                  </div>
                ) : (
                  renderMessageItem(msg)
                )
              )}
            </div>
            <div className="flex-grow"></div> {/* Spacer */}
          </div>

          {/* Image Carousel */}
          {carouselState.isOpen && (
            <ImageCarousel
              items={carouselState.items}
              initialIndex={carouselState.currentIndex}
              onClose={closeCarousel}
              onDeleteItem={handleDeleteCarouselItem}
            />
          )}
        </div>
      )}
    </>
  );
}); // Close forwardRef

ChatBody.displayName = "ChatBody";

export default memo(ChatBody);
